import React, { useCallback } from 'react';
import PropTypes from 'tradera-utils/prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import NextLink from 'next/link';
import { updateEnvironmentHash } from 'tradera-state/environment/actions';
import { useIsFeatureEnabled } from 'tradera-hooks/use-is-feature-enabled';
import { useLocation } from 'tradera-hooks/use-location';
import { isNextJs } from 'tradera-utils/nextjs';
import { URL_LANGUAGES } from 'tradera-lang/constants.mjs';

const PRODUCTION_ORIGIN = 'https://www.tradera.com';
const NEXTWEB_PATHS = ['/how-to-buy', '/how-to-sell'];

const vipPathRegex = new RegExp(
    `(/(${URL_LANGUAGES.join('|')}))?/item/[0-9]+/[0-9]+/`
);
const isVipLink = pathname => vipPathRegex.test(pathname);

const isLinkToNextWebPage = url =>
    url.searchParams.get('next') === '1' ||
    NEXTWEB_PATHS.some(nextwebPath => url.pathname.endsWith(nextwebPath));

const getRelativeUrl = url => `${url.pathname}${url.search}`;

const ALink = ({
    useLink,
    href,
    children,
    onClick: givenOnClick,
    ...otherProps
}) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const isSpaNavigationEnabled = useSelector(
        state => state.environment.isSpaNavigationEnabled
    );
    const isNextWebVipSpaNavigationEnabled = useIsFeatureEnabled(
        'next-web-vip-spa-navigation'
    );

    const handleSpaNavigationClick = useCallback(
        evt => {
            dispatch(updateEnvironmentHash());
            if (givenOnClick) givenOnClick(evt);
        },
        [dispatch, givenOnClick]
    );

    const absoluteUrl = new URL(href, PRODUCTION_ORIGIN);
    const spaNavigationOrigins = [PRODUCTION_ORIGIN, location?.origin];
    const isRelativeUrl = spaNavigationOrigins.includes(absoluteUrl.origin);

    if (isNextWebVipSpaNavigationEnabled && isVipLink(href)) {
        absoluteUrl.searchParams.set('next', '1');
    }

    // Strip away origin for SPA links
    const relativeUrl = getRelativeUrl(absoluteUrl);

    if (isSpaNavigationEnabled && isRelativeUrl) {
        if (isNextJs && isLinkToNextWebPage(absoluteUrl)) {
            // NextJS routing
            return (
                <NextLink href={relativeUrl} prefetch={false}>
                    <a
                        {...otherProps}
                        onClick={handleSpaNavigationClick}
                        onMouseEnter={e => e.stopPropagation()}>
                        {children}
                    </a>
                </NextLink>
            );
        } else if (!isNextJs && !isLinkToNextWebPage(absoluteUrl) && useLink) {
            // react-router-dom
            return (
                <Link
                    to={relativeUrl}
                    {...otherProps}
                    onClick={handleSpaNavigationClick}>
                    {children}
                </Link>
            );
        }
    }

    const url = isRelativeUrl ? relativeUrl : absoluteUrl.toString();
    return (
        <a href={url} onClick={givenOnClick} {...otherProps}>
            {children}
        </a>
    );
};

ALink.propTypes = {
    useLink: PropTypes.bool,
    href: PropTypes.relativeOrAbsoluteUrlString,
    children: PropTypes.node.isRequired,
    onClick: PropTypes.func
};

ALink.defaultProps = {
    useLink: false,
    onClick: null
};

export default ALink;
